import React, { useState, useEffect } from 'react';
import { 
  Loader2, Save, RotateCcw, Globe, Plane, Settings, Gauge, Radio, Zap, Weight, Ruler
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => localStorage.getItem('admin_token');

const AdminAircraft = () => {
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en');
  const { t } = useLanguage();

  useEffect(() => {
    fetchSpecs();
  }, []);

  const fetchSpecs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/specs`);
      const data = await response.json();
      setSpecs(data);
    } catch (err) {
      toast.error('Teknik özellikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSpecs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/specs`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(specs)
      });
      
      if (!response.ok) throw new Error('Kaydetme başarısız');
      
      toast.success(t('admin.toast.saved'));
      setHasChanges(false);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    await fetchSpecs();
    setHasChanges(false);
    toast.success('Değişiklikler geri alındı');
  };

  // Helper to get field name based on language
  const getFieldKey = (baseKey) => {
    return activeLanguage === 'tr' ? `${baseKey}_tr` : baseKey;
  };

  // Helper to get value - for TR, show TR value or empty for input
  const getValue = (baseKey) => {
    if (activeLanguage === 'tr') {
      return specs[`${baseKey}_tr`] || '';
    }
    return specs[baseKey] || '';
  };

  // Field groups for organized display
  const specGroups = [
    {
      title: '⚙️ Temel Teknik Özellikler',
      description: 'Aracın temel fiziksel ve performans özellikleri',
      fields: [
        { key: 'weight', label: 'Maks. Kalkış Ağırlığı', icon: Weight, hasNote: true },
        { key: 'wingspan', label: 'Kanat Açıklığı', icon: Ruler, hasNote: true },
        { key: 'maxSpeed', label: 'Maksimum Hız', icon: Gauge, translate: false },
        { key: 'cruiseSpeed', label: 'Seyir Hızı', icon: Gauge, translate: false },
        { key: 'payloadCapacity', label: 'Yük Kapasitesi', icon: Weight },
        { key: 'operatingAltitude', label: 'Operasyonel İrtifa', icon: Plane },
        { key: 'thrustToWeight', label: 'İtki/Ağırlık Oranı', icon: Zap, translate: false },
      ]
    },
    {
      title: '🔧 Sistem Bileşenleri',
      description: 'Güç sistemi, otopilot ve iletişim sistemleri',
      fields: [
        { key: 'powerplant', label: 'Güç Ünitesi', icon: Zap },
        { key: 'autopilot', label: 'Otopilot Sistemi', icon: Settings, hasNote: true },
        { key: 'communication', label: 'İletişim Sistemi', icon: Radio, hasNote: true },
        { key: 'endurance', label: 'Uçuş Süresi', icon: Gauge },
      ]
    }
  ];

  const tableNotes = [
    { key: 'tableNote_weight', label: 'Ağırlık Notu (Tablo)' },
    { key: 'tableNote_wingspan', label: 'Kanat Açıklığı Notu (Tablo)' },
    { key: 'tableNote_endurance', label: 'Uçuş Süresi Notu (Tablo)' },
    { key: 'tableNote_payload', label: 'Yük Kapasitesi Notu (Tablo)' },
    { key: 'tableNote_altitude', label: 'İrtifa Notu (Tablo)' },
    { key: 'tableNote_thrust', label: 'İtki/Ağırlık Notu (Tablo)' },
  ];

  const designCards = [
    { num: 1, title: 'designCard1_title', desc: 'designCard1_description', label: 'Aerodinamik Tasarım' },
    { num: 2, title: 'designCard2_title', desc: 'designCard2_description', label: 'İtki Sistemi' },
    { num: 3, title: 'designCard3_title', desc: 'designCard3_description', label: 'Otonom Navigasyon' },
    { num: 4, title: 'designCard4_title', desc: 'designCard4_description', label: 'Yük Sistemi' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-aircraft">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: '#F8FAFC' }}>
            <Plane size={28} style={{ color: '#00F0FF' }} />
            Araç Teknik Özellikleri
          </h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
            METE İHA'nın tüm teknik özelliklerini buradan düzenleyebilirsiniz
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-xs px-3 py-1 rounded-full" style={{ 
              background: 'rgba(245, 158, 11, 0.2)', 
              color: '#F59E0B' 
            }}>
              Kaydedilmemiş değişiklikler
            </span>
          )}
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}
          >
            <RotateCcw size={18} />
            Geri Al
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50"
            style={{ background: '#00F0FF', color: '#0F172A' }}
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
            {t('admin.common.save')}
          </button>
        </div>
      </div>

      {/* Language Toggle */}
      <div className="glass-card p-4 mb-6">
        <div className="flex items-center gap-2">
          <Globe size={18} style={{ color: '#94A3B8' }} />
          <span className="text-sm" style={{ color: '#94A3B8' }}>İçerik Dili:</span>
          <div className="flex items-center gap-2 p-1 rounded-lg ml-2" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
            <button
              onClick={() => setActiveLanguage('en')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
              style={{
                background: activeLanguage === 'en' ? 'rgba(0, 240, 255, 0.2)' : 'transparent',
                color: activeLanguage === 'en' ? '#00F0FF' : '#94A3B8',
                border: activeLanguage === 'en' ? '1px solid #00F0FF' : '1px solid transparent'
              }}
            >
              {t('admin.common.english')}
            </button>
            <button
              onClick={() => setActiveLanguage('tr')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
              style={{
                background: activeLanguage === 'tr' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                color: activeLanguage === 'tr' ? '#EF4444' : '#94A3B8',
                border: activeLanguage === 'tr' ? '1px solid #EF4444' : '1px solid transparent'
              }}
            >
              {t('admin.common.turkish')}
            </button>
          </div>
        </div>
        {activeLanguage === 'tr' && (
          <p className="text-xs mt-3 p-3 rounded-lg" style={{ 
            background: 'rgba(239, 68, 68, 0.1)', 
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#F8FAFC' 
          }}>
            💡 Türkçe içerik girdiğinizde, site Türkçe modda bu içerikleri gösterecektir. Boş bırakırsanız İngilizce içerik gösterilir.
          </p>
        )}
      </div>

      {/* Spec Groups */}
      {specGroups.map((group) => (
        <div key={group.title} className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>{group.title}</h2>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>{group.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {group.fields.map((field) => {
              const Icon = field.icon;
              const showTranslation = field.translate !== false;
              const fieldKey = showTranslation ? getFieldKey(field.key) : field.key;
              const noteKey = field.hasNote ? getFieldKey(`${field.key}Note`) : null;
              
              return (
                <div key={field.key} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Icon size={18} style={{ color: '#00F0FF' }} />
                    <label className="text-sm font-medium" style={{ color: '#F8FAFC' }}>
                      {field.label}
                    </label>
                    {activeLanguage === 'tr' && showTranslation && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ 
                        background: 'rgba(239, 68, 68, 0.2)', 
                        color: '#EF4444' 
                      }}>TR</span>
                    )}
                  </div>
                  
                  <input
                    type="text"
                    value={showTranslation ? getValue(field.key) : (specs[field.key] || '')}
                    onChange={(e) => handleChange(showTranslation ? fieldKey : field.key, e.target.value)}
                    disabled={!showTranslation && activeLanguage === 'tr'}
                    className="w-full px-4 py-3 rounded-lg disabled:opacity-50"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: activeLanguage === 'tr' && showTranslation
                        ? '1px solid rgba(239, 68, 68, 0.3)' 
                        : '1px solid rgba(0, 240, 255, 0.3)',
                      color: '#F8FAFC'
                    }}
                    placeholder={activeLanguage === 'tr' && showTranslation ? `Türkçe: ${specs[field.key] || ''}` : ''}
                  />
                  
                  {/* Note field if applicable */}
                  {field.hasNote && (
                    <div className="ml-6">
                      <label className="text-xs" style={{ color: '#64748B' }}>Alt not:</label>
                      <input
                        type="text"
                        value={noteKey ? (activeLanguage === 'tr' ? (specs[noteKey] || '') : (specs[`${field.key}Note`] || '')) : ''}
                        onChange={(e) => handleChange(noteKey || `${field.key}Note`, e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg mt-1"
                        style={{
                          background: 'rgba(15, 23, 42, 0.6)',
                          border: '1px solid rgba(100, 116, 139, 0.3)',
                          color: '#94A3B8'
                        }}
                        placeholder={activeLanguage === 'tr' ? `Türkçe not: ${specs[`${field.key}Note`] || ''}` : 'Ek bilgi...'}
                      />
                    </div>
                  )}
                  
                  {/* Show English reference when editing Turkish */}
                  {activeLanguage === 'tr' && showTranslation && specs[field.key] && (
                    <p className="text-xs ml-6" style={{ color: '#64748B' }}>
                      İngilizce: {specs[field.key]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Table Notes */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>📋 Tablo Notları</h2>
        <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Teknik özellikler tablosunda görüntülenen açıklama notları</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tableNotes.map((note) => {
            const fieldKey = getFieldKey(note.key);
            return (
              <div key={note.key}>
                <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#F8FAFC' }}>
                  {note.label}
                  {activeLanguage === 'tr' && (
                    <span className="text-xs px-2 py-0.5 rounded" style={{ 
                      background: 'rgba(239, 68, 68, 0.2)', 
                      color: '#EF4444' 
                    }}>TR</span>
                  )}
                </label>
                <input
                  type="text"
                  value={getValue(note.key)}
                  onChange={(e) => handleChange(fieldKey, e.target.value)}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: activeLanguage === 'tr' 
                      ? '1px solid rgba(239, 68, 68, 0.3)' 
                      : '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                  placeholder={activeLanguage === 'tr' ? `Türkçe: ${specs[note.key] || ''}` : ''}
                />
                {activeLanguage === 'tr' && specs[note.key] && (
                  <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                    İngilizce: {specs[note.key]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Design Cards */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>🔧 Tasarım Gerekçesi ve Alt Sistemler</h2>
        <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Araç sayfasında gösterilen tasarım kartlarının içerikleri</p>
        
        <div className="space-y-6">
          {designCards.map((card) => (
            <div key={card.num} className="p-4 rounded-lg" style={{ 
              background: 'rgba(15, 23, 42, 0.6)',
              border: '1px solid rgba(0, 240, 255, 0.2)'
            }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold"
                  style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>
                  {card.num}
                </span>
                <span className="font-medium" style={{ color: '#F8FAFC' }}>{card.label}</span>
                {activeLanguage === 'tr' && (
                  <span className="text-xs px-2 py-0.5 rounded" style={{ 
                    background: 'rgba(239, 68, 68, 0.2)', 
                    color: '#EF4444' 
                  }}>TR</span>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs mb-1 block" style={{ color: '#94A3B8' }}>Başlık</label>
                  <input
                    type="text"
                    value={getValue(card.title)}
                    onChange={(e) => handleChange(getFieldKey(card.title), e.target.value)}
                    className="w-full px-4 py-3 rounded-lg"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: activeLanguage === 'tr' 
                        ? '1px solid rgba(239, 68, 68, 0.3)' 
                        : '1px solid rgba(0, 240, 255, 0.3)',
                      color: '#F8FAFC'
                    }}
                    placeholder={activeLanguage === 'tr' ? `Türkçe: ${specs[card.title] || ''}` : ''}
                  />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color: '#94A3B8' }}>Açıklama</label>
                  <textarea
                    value={getValue(card.desc)}
                    onChange={(e) => handleChange(getFieldKey(card.desc), e.target.value)}
                    rows={2}
                    className="w-full px-4 py-3 resize-none rounded-lg"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: activeLanguage === 'tr' 
                        ? '1px solid rgba(239, 68, 68, 0.3)' 
                        : '1px solid rgba(0, 240, 255, 0.3)',
                      color: '#F8FAFC'
                    }}
                    placeholder={activeLanguage === 'tr' ? `Türkçe: ${specs[card.desc] || ''}` : ''}
                  />
                </div>
              </div>
              
              {/* Show English reference when editing Turkish */}
              {activeLanguage === 'tr' && specs[card.title] && (
                <p className="text-xs mt-2" style={{ color: '#64748B' }}>
                  İngilizce: {specs[card.title]} - {specs[card.desc]?.substring(0, 60)}...
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAircraft;
